import { useEffect, useState } from "react";
import { Download, Share, X, Plus } from "lucide-react";

const VISITS_KEY = "yrf_visits";
const DISMISS_KEY = "yrf_pwa_dismiss";
const REMIND_VISITS = 3;
const REMIND_DAYS = 7;

function isStandalone() {
  return (
    (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = window.navigator.userAgent || "";
  const iOS = /iphone|ipad|ipod/i.test(ua);
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOS || iPadOS;
}

function bumpVisits() {
  const n = parseInt(localStorage.getItem(VISITS_KEY) || "0", 10) + 1;
  localStorage.setItem(VISITS_KEY, String(n));
  return n;
}

function allowedToShow(visits) {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return true;
    const d = JSON.parse(raw);
    const days = (Date.now() - (d.ts || 0)) / 86400000;
    return visits - (d.visit || 0) >= REMIND_VISITS || days >= REMIND_DAYS;
  } catch {
    return true;
  }
}

export function InstallPrompt() {
  const [mode, setMode] = useState(null); // 'install' | 'ios' | null
  const [deferred, setDeferred] = useState(null);

  useEffect(() => {
    if (isStandalone()) return;
    const visits = bumpVisits();
    const canShow = allowedToShow(visits);

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (canShow) setMode("install");
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    const onInstalled = () => setMode(null);
    window.addEventListener("appinstalled", onInstalled);

    let t;
    if (isIOS() && canShow) {
      t = setTimeout(() => setMode("ios"), 2500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
      if (t) clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    const visits = parseInt(localStorage.getItem(VISITS_KEY) || "0", 10);
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ visit: visits, ts: Date.now() }));
    setMode(null);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome !== "accepted") dismiss();
    else setMode(null);
    setDeferred(null);
  };

  if (!mode) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed z-[60] left-4 right-4 bottom-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm animate-fade-up"
    >
      <div className="relative flex items-center gap-3 rounded-2xl bg-[#0a1420] border border-[#22d3ee]/30 shadow-2xl p-4 pr-10">
        <img src="/icon-192.png" alt="YourRaceFinder" className="w-12 h-12 rounded-xl shrink-0" />
        <div className="min-w-0">
          <p className="font-display font-bold uppercase tracking-wide text-white text-sm leading-tight">Install YourRaceFinder</p>
          {mode === "install" ? (
            <p className="text-slate-300 text-xs mt-0.5">Add the app to your device for one-tap access to races.</p>
          ) : (
            <p className="text-slate-300 text-xs mt-0.5 flex items-center flex-wrap gap-1">
              Tap <Share size={13} className="inline text-[#22d3ee]" /> then <span className="inline-flex items-center gap-0.5">"Add to Home Screen" <Plus size={12} className="text-[#22d3ee]" /></span>
            </p>
          )}
          {mode === "install" && (
            <button
              onClick={install}
              data-testid="pwa-install-button"
              className="mt-2.5 inline-flex items-center gap-1.5 bg-[#22d3ee] text-[#0a1420] font-bold text-sm px-4 py-2 rounded-lg hover:bg-white transition-colors"
            >
              <Download size={15} /> Install App
            </button>
          )}
        </div>
        <button
          onClick={dismiss}
          data-testid="pwa-dismiss-button"
          aria-label="Dismiss install prompt"
          className="absolute top-2.5 right-2.5 text-slate-400 hover:text-white transition-colors"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

export default InstallPrompt;
